import { Tournament } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { PlatformMap } from '@/src/presentation/components/PlatformMap';
import { FontAwesome5, Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import DateTimePicker, { DateTimePickerAndroid } from '@react-native-community/datetimepicker';
import { useEffect, useState } from 'react';
import { Platform, ScrollView, Switch, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { createStyles } from '../TournamentDetailScreen.styles';

interface SettingsTabProps {
  tournament: Tournament;
  userRole?: string;
  canEditTournament: boolean;
  canManageParticipants: boolean;
  isSetup: boolean;
  inviteCode?: string;
  editingLocation: boolean;
  setEditingLocation: (editing: boolean) => void;
  locationName: string;
  setLocationName: (name: string) => void;
  selectedLocation: { latitude: number; longitude: number } | null;
  setSelectedLocation: (location: { latitude: number; longitude: number } | null) => void;
  editingDate: boolean;
  setEditingDate: (editing: boolean) => void;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  showDatePicker: boolean;
  onSaveLocation: () => void;
  onCancelLocation: () => void;
  onSaveDate: () => void;
  onCancelDate: () => void;
  onTogglePublic: (value: boolean) => void;
  onCopyLink: () => void;
  onShareInvite: () => void;
  onEditTournament: () => void;
  onOpenMaps: () => void;
  onGenerateInviteCode: () => void;
  onCopyInviteCode: () => void;
  onShareInviteCode: () => void;
  onInviteByEmail: () => void;
  colors: {
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    surface: string;
  };
}

export const SettingsTab = ({
  tournament,
  userRole,
  canEditTournament,
  canManageParticipants,
  isSetup,
  inviteCode,
  editingLocation,
  setEditingLocation,
  locationName,
  setLocationName,
  selectedLocation,
  setSelectedLocation,
  editingDate,
  setEditingDate,
  selectedDate,
  setSelectedDate,
  onSaveLocation,
  onCancelLocation,
  onSaveDate,
  onCancelDate,
  onTogglePublic,
  onCopyLink,
  onShareInvite,
  onEditTournament,
  onOpenMaps,
  onGenerateInviteCode,
  onCopyInviteCode,
  onShareInviteCode,
  onInviteByEmail,
  colors,
}: SettingsTabProps) => {
  const styles = createStyles();
  const { t } = useTranslation();
  const [descExpanded, setDescExpanded] = useState(false);

  // Use imperative API on Android to avoid DateTimePickerAndroid.dismiss() crash on unmount
  useEffect(() => {
    if (Platform.OS !== 'android' || !editingDate) return;

    DateTimePickerAndroid.open({
      value: selectedDate,
      mode: 'date',
      onChange: (event, date) => {
        if (event.type === 'dismissed' || !date) { onCancelDate(); return; }
        DateTimePickerAndroid.open({
          value: date,
          mode: 'time',
          is24Hour: true,
          onChange: (timeEvent, timeDate) => {
            if (timeEvent.type === 'dismissed' || !timeDate) { onCancelDate(); return; }
            setSelectedDate(timeDate);
          },
        });
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingDate]);

  return (
    <ScrollView style={styles.tabContainer} contentContainerStyle={styles.settingsContent}>
      <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <Text style={[styles.settingsLabel, { color: colors.textSecondary }]}>{t('tournaments.tournamentType')}</Text>
        {tournament.tournamentType === 'single_elimination' && (
          <View style={styles.tournamentTypeRow}>
            <FontAwesome5 name="trophy" size={14} color={colors.textTertiary} />
            <Text style={[styles.tournamentTypeText, { color: colors.textTertiary }]}>{t('tournaments.singleElimination')}</Text>
          </View>
        )}
        {tournament.tournamentType === 'double_elimination' && (
          <View style={styles.tournamentTypeRow}>
            <MaterialCommunityIcons name="tournament" size={16} color={colors.textTertiary} />
            <Text style={[styles.tournamentTypeText, { color: colors.textTertiary }]}>{t('tournaments.doubleElimination')}</Text>
          </View>
        )}
        {tournament.tournamentType === 'round_robin' && (
          <View style={styles.tournamentTypeRow}>
            <MaterialCommunityIcons name="sync-circle" size={16} color={colors.textTertiary} />
            <Text style={[styles.tournamentTypeText, { color: colors.textTertiary }]}>{t('tournaments.roundRobin')}</Text>
          </View>
        )}
        {tournament.tournamentType === 'swiss' && (
          <View style={styles.tournamentTypeRow}>
            <MaterialCommunityIcons name="grid" size={16} color={colors.textTertiary} />
            <Text style={[styles.tournamentTypeText, { color: colors.textTertiary }]}>{t('tournaments.swiss')}</Text>
          </View>
        )}
      </View>

      <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <Text style={[styles.settingsLabel, { color: colors.textSecondary }]}>{t('tournaments.status')}</Text>
        <Text style={[styles.settingsValue, { color: colors.text }]}>
          {tournament.status === 'setup' ? t('tournaments.setup') : tournament.status === 'in_progress' ? t('tournaments.inProgress') : t('tournaments.completed')}
        </Text>
      </View>

      {tournament.description && (
        <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <Text style={[styles.settingsLabel, { color: colors.textSecondary }]}>{t('tournaments.description')}</Text>
          <View style={!descExpanded ? { maxHeight: 88, overflow: 'hidden' } : undefined}>
            <Text
              style={[styles.settingsValue, { color: colors.text }]}
              numberOfLines={descExpanded ? undefined : 4}
              ellipsizeMode="tail"
            >
              {tournament.description}
            </Text>
          </View>
          {tournament.description.split('\n').length > 4 || tournament.description.length > 200 ? (
            <TouchableOpacity onPress={() => setDescExpanded(!descExpanded)} style={{ marginTop: 4 }}>
              <Text style={{ color: colors.primary, fontSize: 14 }}>
                {descExpanded ? t('common.seeLess') : t('common.seeMore')}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      )}

      <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <Text style={[styles.settingsLabel, { color: colors.textSecondary }]}>{t('tournaments.yourRole')}</Text>
        <Text style={[styles.settingsValue, { color: colors.text }]}>
          {userRole === 'owner' ? t('tournaments.owner') : userRole === 'admin' ? t('tournaments.admin') : t('tournaments.viewer')}
        </Text>
      </View>

      {/* Location Section */}
      {canEditTournament && (
        <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <Text style={[styles.settingsLabel, { color: colors.textSecondary }]}>{t('tournaments.location')}</Text>
          {editingLocation ? (
            <>
              <TextInput
                style={[
                  styles.input,
                  { backgroundColor: colors.surface, borderColor: colors.border, color: colors.text, marginBottom: 10 },
                ]}
                placeholder={t('tournaments.locationPlaceholder')}
                placeholderTextColor={colors.textTertiary}
                value={locationName}
                onChangeText={setLocationName}
              />

              <PlatformMap
                style={styles.map}
                initialRegion={{
                  latitude: selectedLocation?.latitude || 40.7128,
                  longitude: selectedLocation?.longitude || -74.006,
                  latitudeDelta: 0.05,
                  longitudeDelta: 0.05,
                }}
                onPress={(coordinate) => setSelectedLocation(coordinate)}
                markerPosition={selectedLocation}
              />

              <Text style={[styles.mapHelper, { color: colors.textTertiary }]}>{t('tournaments.tapMapToSetLocation')}</Text>

              <View style={styles.rulesButtons}>
                <TouchableOpacity
                  style={[styles.rulesSaveButton, { backgroundColor: colors.primary }]}
                  onPress={onSaveLocation}
                >
                  <Text style={[styles.rulesButtonText, { color: colors.primaryText }]}>{t('tournaments.saveLocation')}</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.rulesCancelButton, { borderColor: colors.border }]}
                  onPress={onCancelLocation}
                >
                  <Text style={[styles.rulesButtonText, { color: colors.text }]}>{t('common.cancel')}</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <>
              {tournament?.location ? (
                <>
                  <Text style={[styles.settingsValue, { color: colors.text }]}>{tournament.location}</Text>
                  {tournament.locationLat && tournament.locationLng && (
                    <TouchableOpacity
                      style={[styles.openMapsButton, { backgroundColor: colors.primary, marginTop: 10 }]}
                      onPress={onOpenMaps}
                    >
                      <Ionicons name="location" size={18} color={colors.primaryText} style={{ marginRight: 6 }} />
                      <Text style={[styles.openMapsButtonText, { color: colors.primaryText }]}>{t('tournaments.openInMaps')}</Text>
                    </TouchableOpacity>
                  )}
                </>
              ) : (
                <Text style={[styles.settingsValue, { color: colors.textTertiary }]}>{t('tournaments.notSet')}</Text>
              )}
              <TouchableOpacity
                style={[styles.editButton, { marginTop: 10 }]}
                onPress={() => setEditingLocation(true)}
              >
                <Text style={[styles.editButtonText, { color: colors.primary }]}>
                  {tournament?.location ? t('tournaments.editLocation') : t('tournaments.addLocation')}
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      )}

      {/* Date & Time Section */}
      {canEditTournament && (
        <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <Text style={[styles.settingsLabel, { color: colors.textSecondary }]}>{t('tournaments.startDateTime')}</Text>
          {editingDate ? (
            <>
              {Platform.OS === 'web' ? (
                <input
                  type="datetime-local"
                  value={selectedDate.toISOString().slice(0, 16)}
                  onChange={(e: any) => {
                    const date = new Date(e.target.value);
                    if (!isNaN(date.getTime())) setSelectedDate(date);
                  }}
                  style={{
                    padding: 12,
                    fontSize: 16,
                    borderRadius: 8,
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.surface,
                    color: colors.text,
                    marginBottom: 10,
                  }}
                />
              ) : Platform.OS === 'android' ? (
                <Text style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 8 }}>
                  {selectedDate.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                </Text>
              ) : (
                <DateTimePicker
                  value={selectedDate}
                  mode="datetime"
                  display="default"
                  onChange={(event, date) => {
                    if (!date) return;
                    setSelectedDate(date);
                  }}
                />
              )}

              <View style={styles.rulesButtons}>
                <TouchableOpacity
                  style={[styles.rulesSaveButton, { backgroundColor: colors.primary }]}
                  onPress={onSaveDate}
                >
                  <Text style={[styles.rulesButtonText, { color: colors.primaryText }]}>{t('tournaments.saveDate')}</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.rulesCancelButton, { borderColor: colors.border }]}
                  onPress={onCancelDate}
                >
                  <Text style={[styles.rulesButtonText, { color: colors.text }]}>{t('common.cancel')}</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <>
              <Text style={[styles.settingsValue, { color: colors.text }]}>
                {tournament?.startDate
                  ? new Date(tournament.startDate).toLocaleString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : t('tournaments.notSet')}
              </Text>
              <TouchableOpacity style={[styles.editButton, { marginTop: 10 }]} onPress={() => setEditingDate(true)}>
                <Text style={[styles.editButtonText, { color: colors.primary }]}>
                  {tournament?.startDate ? t('tournaments.editDate') : t('tournaments.addDate')}
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      )}

      {canEditTournament && (
        <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.settingRow}>
            <View style={styles.settingInfo}>
              <Text style={[styles.settingsLabel, { color: colors.textSecondary }]}>{t('tournaments.publicTournament')}</Text>
              <Text style={[styles.settingDescription, { color: colors.textTertiary }]}>
                {t('tournaments.publicTournamentDesc')}
              </Text>
            </View>
            <Switch
              value={tournament.isPublic || false}
              onValueChange={onTogglePublic}
              trackColor={{ false: '#767577', true: colors.primary }}
              thumbColor="#f4f3f4"
            />
          </View>
        </View>
      )}

      {canEditTournament && tournament.isPublic && (
        <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <Text style={[styles.settingsLabel, { color: colors.textSecondary }]}>{t('tournaments.inviteParticipants')}</Text>
          <Text style={[styles.settingDescription, { color: colors.textTertiary, marginBottom: 12 }]}>
            {t('tournaments.shareInviteDesc')}
          </Text>
          <TouchableOpacity
            style={[styles.shareInviteButton, { backgroundColor: colors.primary }]}
            onPress={onShareInvite}
          >
            <Ionicons name="share-outline" size={20} color={colors.primaryText} style={{ marginRight: 8 }} />
            <Text style={[styles.shareInviteButtonText, { color: colors.primaryText }]}>{t('tournaments.shareInviteLink')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.copyButton, { backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }]}
            onPress={onCopyLink}
          >
            <Ionicons name="copy-outline" size={18} color={colors.text} style={{ marginRight: 6 }} />
            <Text style={[styles.copyButtonText, { color: colors.text }]}>{t('tournaments.copyLink')}</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Invite Code Section */}
      {canManageParticipants && isSetup && (
        <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <Text style={[styles.settingsLabel, { color: colors.textSecondary }]}>{t('invitations.inviteCode')}</Text>
          <Text style={[styles.settingDescription, { color: colors.textTertiary, marginBottom: 12 }]}>
            {t('invitations.inviteCodeDesc')}
          </Text>
          {inviteCode ? (
            <>
              <View style={[styles.inviteCodeDisplay, { backgroundColor: colors.surface }]}>
                <Text style={[styles.inviteCodeText, { color: colors.text }]}>{inviteCode}</Text>
              </View>
              <View style={styles.inviteCodeActions}>
                <TouchableOpacity
                  style={[styles.inviteCodeButton, { backgroundColor: colors.primary }]}
                  onPress={onCopyInviteCode}
                >
                  <Ionicons name="copy-outline" size={18} color={colors.primaryText} />
                  <Text style={[styles.inviteCodeButtonText, { color: colors.primaryText }]}>{t('common.copy')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.inviteCodeButton, { backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }]}
                  onPress={onShareInviteCode}
                >
                  <Ionicons name="share-outline" size={18} color={colors.text} />
                  <Text style={[styles.inviteCodeButtonText, { color: colors.text }]}>{t('common.share')}</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <TouchableOpacity
              style={[styles.shareInviteButton, { backgroundColor: colors.primary }]}
              onPress={onGenerateInviteCode}
            >
              <Ionicons name="key-outline" size={20} color={colors.primaryText} style={{ marginRight: 8 }} />
              <Text style={[styles.shareInviteButtonText, { color: colors.primaryText }]}>{t('invitations.generateCode')}</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Invite by Email Section */}
      {canManageParticipants && isSetup && (
        <View style={[styles.settingsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <Text style={[styles.settingsLabel, { color: colors.textSecondary }]}>{t('invitations.inviteByEmail')}</Text>
          <Text style={[styles.settingDescription, { color: colors.textTertiary, marginBottom: 12 }]}>
            {t('invitations.inviteByEmailDesc')}
          </Text>
          <TouchableOpacity
            style={[styles.shareInviteButton, { backgroundColor: colors.primary }]}
            onPress={onInviteByEmail}
          >
            <Ionicons name="mail-outline" size={20} color={colors.primaryText} style={{ marginRight: 8 }} />
            <Text style={[styles.shareInviteButtonText, { color: colors.primaryText }]}>{t('invitations.sendInvitation')}</Text>
          </TouchableOpacity>
        </View>
      )}

      {canEditTournament && isSetup && (
        <TouchableOpacity
          style={[styles.actionButton, { backgroundColor: colors.primary }]}
          onPress={onEditTournament}
        >
          <Text style={[styles.actionButtonText, { color: colors.primaryText }]}>{t('tournaments.editTournament')}</Text>
        </TouchableOpacity>
      )}
    </ScrollView>
  );
};
